import { Coordinate, IGameServer, InitialResponse, SpinResponse } from "src/shared/util/Types";
import { GameState, GlobalState } from "src/shared/util/GlobalState";
import { observer } from "src/shared/util/Observer";
import { EngineMock } from "./EngineMock";

export interface SymbolData {
    type: string;
    coordinates: Coordinate;
}

export class GameEngine {
    private static instance: GameEngine;
    private data!: SpinResponse;
    private loadResponse!: InitialResponse;

    public config = {
        reels: 6,
        rows: 5,
        isCascade: true,
        isMock: true,
        winType: "scatter",
    };
    protected formatter!: Intl.NumberFormat;
    protected client: IGameServer;
    protected counter: number = 0;

    constructor() {
        // Use EngineMock for testing purposes
        if (this.config.isMock) {
            this.client = new EngineMock();
        } else {
            this.client = new EngineMock();
            // this.client = new SignalrClient("https://slotgamesapi.yogames.win/slots");
        }

        // Set up event listeners for initial state and game action results
        this.client.onInitialStateResult((data: InitialResponse) => {
            this.loadResponse = data;
            GlobalState.getInstance().updateState({ bet: data.bet ? data.bet : 1, balance: data.balance, state: data.state });
            observer.publish('initialStateResponse', data);
        });

        this.client.onGameActionResult((data: SpinResponse) => {
            this.data = data;
            let balance: number = data.balance || GlobalState.getInstance().balance;

            // // Update balance based on game state and wins
            // if (data.state === GameState.BONUS_TO_MAIN && data.totalWin) {
            //     balance += data.totalWin;
            // } else if (data.roundWin && data.state === GameState.MAIN && data.wins.length === 0) {
            //     balance += data.roundWin;
            // } else if (data.state === GameState.MAIN_TO_BONUS && data.roundWin) {
            //     // Add this condition to handle scatter wins when transitioning to bonus
            //     balance += data.roundWin;
            // }

            GlobalState.getInstance().updateState({ balance, state: data.state });
            observer.publish('gameActionResponse', data);
        });

        // Initialize number formatter
        this.formatter = new Intl.NumberFormat('en', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    // Public method to get the singleton instance
    public static getInstance(): GameEngine {
        if (!GameEngine.instance) {
            GameEngine.instance = new GameEngine();
        }
        return GameEngine.instance;
    }

    public getFormatter() {
        return this.formatter;
    }

    // Initialize the game engine
    public async init() {
        return new Promise<void>((resolve) => {
            this.client.connect().then(() => {
                observer.once('initialStateResponse', resolve);
                this.client.getInitialState("SweetBonanza", false, 1);
            });
        });
    }

    // Handle game action requests
    public gameActionRequest() {
        if (this.config.isCascade) {
            // If it's a cascade engine, keep the existing logic
            if (this.data) {
                if (this.data.wins.length) {
                    this.cascade();
                } else {
                    this.spin();
                }
            } else {
                this.spin();
            }
        } else {
            // If it's not a cascade engine, always call spin
            this.spin();
        }
    }

    // Handle bonus buy action
    public buy() {
        this.client.gameAction("Spin", {
            BuyBonus: true,   // Whether to buy a bonus spin
            DoubleChance: false // Whether double chance is enabled
        });
    }

    // Perform a spin action
    private spin() {
        if (GlobalState.getInstance().state === GameState.BONUS || GlobalState.getInstance().state === GameState.MAIN_TO_BONUS) {
            this.client.gameAction("BonusSpin", {
                BuyBonus: false,   // Whether to buy a bonus spin
                DoubleChance: false // Whether double chance is enabled
            });
        } else {
            this.client.gameAction("Spin", {
                BuyBonus: false,   // Whether to buy a bonus spin
                DoubleChance: false // Whether double chance is enabled
            });
        }
    }

    // Perform a cascade action
    private cascade() {
        if (GlobalState.getInstance().state === GameState.BONUS) {
            this.client.gameAction("BonusSpinAvalanche");
        } else {
            this.client.gameAction("SpinAvalanche");
        }
    }

    // Get the latest game response
    public getResponse() {
        return this.data;
    }

    // Get the initial load response
    public getLoadResponse() {
        return this.loadResponse;
    }
}
